import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge, Spinner } from "reactstrap"

// Common Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

// Local Components
import AccessoriesAddEdit from "./AccessoriesUsageAddEdit"
import AccessoriesUsageHeader from "./AccessoriesUsageHeader"

// CRUD Hooks
import {
  useAccessoriesUsages,
  useDeleteAccessoriesUsage,
} from "hooks/useAccessoriesUsage"

// Utils
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { calculateTotalPrice, calculateBillTotal } from "utils/accessoryUtils"
import AccessoriesUsageViewModal from "./AccessoriesUsageView"
import { getAccessoriesUsageBillPdfById } from "api/accessoriesUsageApi"

const AccessoriesUsageList = () => {
  document.title = "Accessories Usage List"

  // -------------------- STATE --------------------
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [isView, setIsView] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [selectedViewData, setSelectedViewData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [downloadingBillId, setDownloadingBillId] = useState(null)

  // -------------------- LIST PARAMS --------------------
  const [params, setParams] = useState({ page: 1, per_page: 10, search: "" })
  const setPage = page => setParams(prev => ({ ...prev, page }))
  const setSearch = search => setParams(prev => ({ ...prev, search, page: 1 }))

  // -------------------- API --------------------
  const {
    data: responseData,
    isLoading,
    refetch,
    isFetching,
  } = useAccessoriesUsages(params)
  const { mutateAsync: deleteItem, isLoading: deleteLoading } =
    useDeleteAccessoriesUsage()

  // -------------------- BILL DOWNLOAD HANDLER --------------------
  const handleBillDownload = useCallback(async (id) => {
    setDownloadingBillId(id)
    try {
      await getAccessoriesUsageBillPdfById(id)
    } catch (err) {
      console.error(err)
      toast.error("Failed to download invoice.")
    } finally {
      setDownloadingBillId(null)
    }
  }, [])

  // -------------------- MODAL HANDLERS --------------------
  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewData(item)
    setIsView(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Accessories stock deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete accessories stock.")
    }
  }

  // -------------------- TABLE COLUMNS --------------------
  const columns = useMemo(
  () => [
    {
      header: "Reference",
      accessorKey: "reference_no",
      cell: ({ row }) => row.original.reference_no || row.original.id,
    },
    {
      header: "Source",
      accessorKey: "source",
      cell: ({ row }) =>
        row.original.source === "service_invoice" ? (
          <Badge color="warning">Service Invoice</Badge>
        ) : (
          <Badge color="info">Usage</Badge>
        ),
    },
    {
      header: "Usage Date",
      accessorKey: "usage_date",
      cell: ({ row }) =>
        row.original.usage_date
          ? formatReadableDate(row.original.usage_date)
          : "-",
    },
    {
      header: "Used By",
      accessorKey: "usage_by.name",
      cell: ({ row }) => row.original.usage_by?.name || "-",
    },
    {
      header: "Vehicle",
      accessorKey: "vehicle",
      cell: ({ row }) => {
        const vehicle = row.original.vehicle
        if (!vehicle) return "-"
        return (
          <>
            <div>{vehicle.stock_id}</div>
            <div className="text-muted" style={{ fontSize: "0.85rem" }}>
              {vehicle.full_chassis}
            </div>
          </>
        )
      },
    },
    {
      header: "Total Accessories",
      accessorKey: "items",
      cell: ({ row }) => <Badge color="info">{row.original.items?.length || 0}</Badge>,
    },
    {
      header: "Invoice Total",
      accessorKey: "bill_type",
      cell: ({ row }) => (
        <div>
          {formatBDT(calculateBillTotal(row.original))}
          {row.original.bill_type === "package" && (
            <Badge color="secondary" className="ms-1">Package</Badge>
          )}
        </div>
      ),
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      cell: ({ row }) =>
        row.original.created_at
          ? formatReadableDate(row.original.created_at)
          : "-",
    },
    {
      header: "Action",
      cell: ({ row }) => {
        const data = row.original
        const uid = data.uid || data.id
        // Service-invoice rows are read-only here — managed from Service Invoices.
        const isInvoice = data.source === "service_invoice"
        return (
          <div className="d-flex flex-wrap gap-1">
            <Button
              color="primary"
              size="sm"
              onClick={() => handleViewClick(data)}
              id={`viewtooltip-${uid}`}
            >
              <i className="mdi mdi-eye" />
            </Button>
            <UncontrolledTooltip placement="top" target={`viewtooltip-${uid}`}>
              View
            </UncontrolledTooltip>

            {!isInvoice && (
              <>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => handleEditClick(data)}
                  id={`edit-${uid}`}
                >
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip target={`edit-${uid}`}>Edit</UncontrolledTooltip>

                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleDeleteClick(data)}
                  id={`delete-${uid}`}
                >
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip target={`delete-${uid}`}>Delete</UncontrolledTooltip>

                <Button
                  color="success"
                  size="sm"
                  onClick={() => handleBillDownload(data.id)}
                  id={`bill-${uid}`}
                  disabled={downloadingBillId === data.id}
                >
                  {downloadingBillId === data.id ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="mdi mdi-file-document-outline" />
                  )}
                </Button>
                <UncontrolledTooltip target={`bill-${uid}`}>
                  Download Bill
                </UncontrolledTooltip>
              </>
            )}
          </div>
        )
      },
    },
  ],
  [downloadingBillId]
)


  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  // -------------------- RENDER --------------------
  return (
    <PageWrapper
      title="Accessories Usage List"
      breadcrumbItem="Accessories Usage List"
    >
      <AccessoriesUsageHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={() => setParams({ page: 1, per_page: 10, search: "" })}
        isRefetching={isFetching}
        hasActiveFilters={!!params.search}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isFetching}
        className="table-responsive table-hover"
      />

      <AccessoriesAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
        onSuccess={refetch}
      />

      <AccessoriesUsageViewModal
        isOpen={isView}
        data={selectedViewData}
        toggle={() => {
          setIsView(false)
          setSelectedViewData(null)
        }}
      />
    </PageWrapper>
  )
}

export default AccessoriesUsageList
